import { cn } from "../ui/cn";

/**
 * AnalyticsStatCard Component
 * Stat display card matching AdminDashboard style with hover effects
 *
 * @description Shared stat card for analytics dashboards (admin and provider)
 */

interface AnalyticsStatCardProps {
  readonly label: string;
  readonly value: string;
  readonly subtitle?: string;
  readonly icon?: React.ReactNode;
  /** Optional trend percentage (positive = green, negative = red) */
  readonly trend?: number;
  /** Use compact variant for dense layouts */
  readonly compact?: boolean;
}

export function AnalyticsStatCard({ label, value, subtitle, icon, trend, compact }: AnalyticsStatCardProps) {
  if (compact) {
    return (
      <div className="bg-neutral-900/50 border border-neutral-800 rounded-lg px-4 py-3 hover:bg-neutral-800/50 transition-all">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-medium text-neutral-400 truncate">{label}</span>
          {trend !== undefined && (
            <span
              className={cn(
                "text-[10px] font-medium px-1.5 py-0.5 rounded",
                trend >= 0 ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400",
              )}
            >
              {trend >= 0 ? "+" : ""}
              {trend}%
            </span>
          )}
        </div>
        <div className="text-xl font-bold text-white mt-1">{value}</div>
        {subtitle && <div className="text-[10px] text-neutral-500 mt-0.5 truncate">{subtitle}</div>}
      </div>
    );
  }

  return (
    <div className="bg-neutral-900/50 border border-neutral-800 rounded-lg p-4 hover:bg-neutral-800/50 transition-all">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-neutral-400">{label}</span>
            {trend !== undefined && (
              <span
                className={cn(
                  "text-[10px] font-medium px-1.5 py-0.5 rounded",
                  trend >= 0 ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400",
                )}
              >
                {trend >= 0 ? "+" : ""}
                {trend}%
              </span>
            )}
          </div>
          <div className="text-2xl font-bold text-white mt-1 tracking-tight">{value}</div>
          {subtitle && <div className="text-[10px] text-neutral-500 mt-1 truncate">{subtitle}</div>}
        </div>
        {icon && <div className="text-neutral-600 ml-2 flex-shrink-0">{icon}</div>}
      </div>
    </div>
  );
}

/**
 * CompactMetricRow - Inline metric display for dense lists
 * Used for showing multiple metrics in a compact horizontal layout
 */
interface CompactMetricRowProps {
  readonly items: Array<{
    label: string;
    value: string | number;
    color?: "default" | "green" | "red" | "amber" | "blue";
  }>;
}

export function CompactMetricRow({ items }: CompactMetricRowProps) {
  const colorMap = {
    default: "text-white",
    green: "text-green-400",
    red: "text-red-400",
    amber: "text-amber-400",
    blue: "text-blue-400",
  };

  return (
    <div className="flex items-center gap-6 px-4 py-2 bg-neutral-900/30 rounded-lg">
      {items.map((item) => (
        <div key={item.label} className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wide text-neutral-500">{item.label}</span>
          <span className={cn("text-sm font-semibold", colorMap[item.color || "default"])}>{item.value}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * MiniProgressBar - Compact progress indicator
 */
interface MiniProgressBarProps {
  readonly label: string;
  readonly value: number;
  readonly max?: number;
  readonly color?: "green" | "amber" | "red" | "blue" | "golden";
  readonly showValue?: boolean;
}

export function MiniProgressBar({ label, value, max = 100, color = "golden", showValue = true }: MiniProgressBarProps) {
  const percent = Math.min((value / max) * 100, 100);
  const colorMap = {
    green: "bg-green-500",
    amber: "bg-amber-500",
    red: "bg-red-500",
    blue: "bg-blue-500",
    golden: "bg-lumi-accent",
  };

  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-neutral-400 w-20 truncate">{label}</span>
      <div className="flex-1 h-1.5 bg-neutral-800 rounded-full overflow-hidden">
        <div className={cn("h-full rounded-full transition-all", colorMap[color])} style={{ width: `${percent}%` }} />
      </div>
      {showValue && <span className="text-xs font-medium text-neutral-300 w-12 text-right">{value}%</span>}
    </div>
  );
}
